/**
 * Language-aware site search index for SAS.
 *
 * - No backend. Pure frontend static index.
 * - Two separate indexes: AR and EN. A query on `/ar` only hits Arabic
 *   content; a query on `/en` only hits English content.
 * - Each entry has a locale-relative `path` (e.g. `/tools`, `/articles/<slug>`).
 *   The SiteSearch component prefixes it with the active `/ar` or `/en`
 *   so results always stay inside the active route tree.
 */

import {
  TOOLS as _SEED_TOOLS,
  TOOL_CATEGORIES as _SEED_TOOL_CATEGORIES,
} from "@/data/tools";
import { getToolNameAr } from "@/data/toolNamesAr";
import {
  ARTICLES as _SEED_ARTICLES,
  ARTICLE_CATEGORIES as _SEED_ARTICLE_CATEGORIES,
} from "@/data/articles";

/* -----------------------------------------------------------------------
 * Text normalization
 * -------------------------------------------------------------------- */

// Arabic diacritics (tashkeel) + tatweel + superscript alef
const AR_DIACRITICS_RE = /[\u064B-\u0652\u0670\u0640]/g;
// Common letter variants we normalize for forgiving search
const AR_NORMALIZE_MAP = [
  [/[أإآٱ]/g, "ا"],
  [/ة/g, "ه"],
  [/ى/g, "ي"],
  [/ؤ/g, "و"],
  [/ئ/g, "ي"],
];

/** Lightweight normalization: lowercase + strip diacritics + unify AR variants. */
export function normalize(value) {
  if (value == null) return "";
  let s = String(value).toLowerCase().normalize("NFKC");
  s = s.replace(AR_DIACRITICS_RE, "");
  for (const [re, rep] of AR_NORMALIZE_MAP) s = s.replace(re, rep);
  // collapse whitespace
  s = s.replace(/\s+/g, " ").trim();
  return s;
}

/* -----------------------------------------------------------------------
 * Bilingual entries
 *
 * Each entry is declared once with `{ ar, en }` variants for title,
 * subtitle and body. We later split into two language-specific indexes.
 * -------------------------------------------------------------------- */

// Section hash targets — the Home page sections already have `data-testid`
// anchors so linking to them is safe; we use plain pathname navigation.
// For search results we keep it simple and always link to the page top.

const PAGE_ENTRIES = [
  {
    id: "page-home",
    type: "page",
    path: "/",
    title: { ar: "الرئيسية", en: "Home" },
    subtitle: {
      ar: "مجتمع الذكاء الاصطناعي السوري — نظرة عامة",
      en: "Syrian AI Society — overview",
    },
    body: {
      ar: "مبادرة تطوعية غير ربحية لنشر الوعي العملي بالذكاء الاصطناعي في الجامعات والمجتمع السوري عبر ورشات تعليمية ومحتوى عربي مبسط وأدوات مجانية. رؤية 2026 ماذا نقدم من نحن الأثر الفريق",
      en: "A non-profit volunteer initiative raising practical awareness of AI in Syrian universities and community spaces through hands-on workshops, simplified Arabic content, and free tools. Vision 2026 what we do who we are impact team",
    },
    keywords: {
      ar: "الرئيسية المبادرة الذكاء الاصطناعي سوريا رؤية 2026",
      en: "home landing initiative AI Syria vision 2026",
    },
  },
  {
    id: "page-about",
    type: "page",
    path: "/about",
    title: { ar: "عن المبادرة", en: "About" },
    subtitle: {
      ar: "عن مجتمع الذكاء الاصطناعي السوري",
      en: "About the Syrian AI Society",
    },
    body: {
      ar: "من نحن لماذا أطلقنا SAS رسالتنا رؤيتنا قيمنا التمكين البساطة الاعتمادية العمل الجماعي من نخدم لماذا الأدوات المجانية المرحلة القادمة دعوة للتعاون",
      en: "Who we are why we launched SAS our mission our vision our values empowerment simplicity reliability collaboration who we serve why free tools next phase invitation to collaborate",
    },
    keywords: {
      ar: "عن المبادرة رسالة رؤية قيم تمكين بساطة",
      en: "about mission vision values empowerment simplicity reliability collaboration",
    },
  },
  {
    id: "page-tools",
    type: "page",
    path: "/tools",
    title: { ar: "دليل الأدوات", en: "AI Tools" },
    subtitle: {
      ar: "دليل أدوات الذكاء الاصطناعي المجانية",
      en: "Free AI tools directory",
    },
    body: {
      ar: "مجموعة مختارة من أدوات الذكاء الاصطناعي المجانية للطلاب والباحثين والمعلمين محادثة مساعدة بحث علمي عروض تقديمية كتابة إنتاجية تعلم الإنجليزية",
      en: "A curated selection of free AI tools for students researchers educators general assistants research presentations writing productivity learning English",
    },
    keywords: {
      ar: "أدوات ذكاء اصطناعي مجانية دليل تطبيقات",
      en: "tools directory free AI applications utilities",
    },
  },
  {
    id: "page-workshops",
    type: "page",
    path: "/workshops",
    title: { ar: "الورشات", en: "Workshops" },
    subtitle: {
      ar: "الورشات والبرامج التعليمية",
      en: "Workshops & educational programmes",
    },
    body: {
      ar: "نموذج التنفيذ محاضرة توعوية ورشة تطبيقية سلسلة تدريبية الورشات القادمة سلاسل تعليمية ورشات سابقة استضافة ورشة تعاون مؤسسي جامعة حلب",
      en: "Delivery model awareness lecture practical workshop training series upcoming workshops past workshops host a workshop institutional partnership university of Aleppo",
    },
    keywords: {
      ar: "ورشات محاضرات تدريب جامعات كليات استضافة",
      en: "workshops lectures training universities faculties host",
    },
  },
  {
    id: "page-impact",
    type: "page",
    path: "/impact",
    title: { ar: "الأثر", en: "Impact" },
    subtitle: {
      ar: "الأثر والإنجازات",
      en: "Impact and milestones",
    },
    body: {
      ar: "10 محاضرات منفذة 4 داخل جامعة حلب 6 خارج الجامعة 1000 مشارك 460 في السلسلة الجامعية الرئيسية 95% معدل الرضا قراءة الأرقام",
      en: "10 lectures delivered 4 at the university of Aleppo 6 outside the university 1000 participants 460 in the main university series 95% satisfaction reading the numbers",
    },
    keywords: {
      ar: "إحصاءات أرقام مشاركين رضا أثر إنجازات",
      en: "statistics numbers participants satisfaction impact milestones",
    },
  },
  {
    id: "page-team",
    type: "page",
    path: "/team",
    title: { ar: "الفريق", en: "Team" },
    subtitle: { ar: "فريق SAS", en: "The SAS team" },
    body: {
      ar: "فريق تطوعي متكامل تخطيط البرامج تطوير المحتوى تحليل الأثر التنسيق مع الجامعات المؤسس المنسق العام المستشار التقني مسؤول التفاعل الطلابي محلل البيانات الدعم المحلي محمد نور خيرو عماد الدين المصري عامر العلي أحمد جاويش حجة محمد صالح أبي زيد",
      en: "Integrated volunteer team programme planning content development impact analysis coordination with universities founder general coordinator technical advisor student engagement lead data analyst local support Mohammad Nour Khayrou Imad Al-Deen Al-Masri Amer Al-Ali Ahmad Jawish Hajja Mohammad Saleh Abi Zaid",
    },
    keywords: {
      ar: "فريق تطوعي مؤسس منسق مستشار تقني محلل بيانات",
      en: "team volunteers founder coordinator technical advisor data analyst",
    },
  },
  {
    id: "page-contact",
    type: "page",
    path: "/contact",
    title: { ar: "تواصل", en: "Contact" },
    subtitle: { ar: "تواصل معنا", en: "Get in touch" },
    body: {
      ar: "للتعاون والشراكات لطلب استضافة ورشة للتواصل العام للانضمام كمساهم أو متطوع جامعات مدارس منظمات مبادرات",
      en: "Partnerships collaboration host a workshop general inquiries join as contributor volunteer universities schools organisations initiatives",
    },
    keywords: {
      ar: "تواصل شراكة استضافة متطوع",
      en: "contact partnership host volunteer",
    },
  },
  {
    id: "page-articles",
    type: "page",
    path: "/articles",
    title: { ar: "المقالات", en: "Articles" },
    subtitle: {
      ar: "محتوى عربي مبسط عن الذكاء الاصطناعي",
      en: "Simplified Arabic-first content on AI",
    },
    body: {
      ar: "مقالات شروحات أدلة عملية طالب باحث معلم بحث دراسة حياة يومية البحث العلمي التعليم الدراسة أدوات تطبيقات مفاهيم أساسية",
      en: "Articles explainers practical guides student researcher educator research study daily life research learning study tools applications fundamentals",
    },
    keywords: {
      ar: "مقالات محتوى عربي شروحات أدلة",
      en: "articles content explainers guides",
    },
  },
  {
    id: "page-built-with-emergent",
    type: "page",
    path: "/built-with-emergent",
    title: { ar: "تم بناء المنصة باستخدام Emergent", en: "Built with Emergent" },
    subtitle: {
      ar: "ملحوظة تقنية · توثيق البناء",
      en: "Technical note · Build documentation",
    },
    body: {
      ar: "لماذا استخدمنا Emergent لماذا نرشحها بناء المنصة ذكاء اصطناعي عملي ومسؤول",
      en: "Why we used Emergent why we recommend it building the platform practical and responsible AI",
    },
    keywords: {
      ar: "Emergent بناء المنصة توثيق تقني",
      en: "Emergent build platform technical documentation",
    },
  },
];

/* -----------------------------------------------------------------------
 * Tool entries builder — called with live tool catalogue from the CMS.
 * -------------------------------------------------------------------- */
function buildToolEntries(tools, toolCategories) {
  return (tools || []).map((t) => {
    const catIds = t.categories || [];
    const primary = catIds.length ? (toolCategories || []).find((c) => c.id === catIds[0]) : null;
    const catLabelsAr = catIds
      .map((id) => (toolCategories || []).find((c) => c.id === id)?.label?.ar)
      .filter(Boolean)
      .join(" ");
    const catLabelsEn = catIds
      .map((id) => (toolCategories || []).find((c) => c.id === id)?.label?.en)
      .filter(Boolean)
      .join(" ");
    // Curated Arabic display name (transliteration / native phrasing).
    // Indexed alongside the English brand so Arabic-name queries match too.
    const nameAr = getToolNameAr(t.slug);
    return {
      id: `tool-${t.slug}`,
      type: "tool",
      path: "/tools",
      title: { ar: nameAr || t.name, en: t.name },
      subtitle: {
        ar: primary ? primary.label.ar : "",
        en: primary ? primary.label.en : "",
      },
      body: {
        ar: `${t.desc?.ar || ""} ${t.audience?.ar || ""} ${t.whenToUse?.ar || ""}`,
        en: `${t.desc?.en || ""} ${t.audience?.en || ""} ${t.whenToUse?.en || ""}`,
      },
      keywords: {
        ar: `${t.name} ${nameAr} ${catLabelsAr} أداة مجانية`,
        en: `${t.name} ${catLabelsEn} free tool`,
      },
    };
  });
}

const TOOL_ENTRIES = buildToolEntries(_SEED_TOOLS, _SEED_TOOL_CATEGORIES);

/* -----------------------------------------------------------------------
 * Article entries builder — called with live article catalogue from the CMS.
 * Indexes title + excerpt + summary + headings/paragraphs/takeaways.
 * -------------------------------------------------------------------- */
function articleBodyText(article, lang) {
  const parts = [];
  for (const block of article?.body || []) {
    if (block.type === "heading" || block.type === "paragraph" || block.type === "takeaway") {
      const t = block.text?.[lang];
      if (t) parts.push(t);
    }
  }
  return parts.join(" ");
}

function buildArticleEntries(articles, articleCategories) {
  return (articles || []).map((a) => {
    const cat = (articleCategories || []).find((c) => c.id === a.category);
    return {
      id: `article-${a.slug}`,
      type: "article",
      path: `/articles/${a.slug}`,
      title: { ar: a.title?.ar || "", en: a.title?.en || "" },
      subtitle: {
        ar: cat ? cat.label.ar : "مقالة",
        en: cat ? cat.label.en : "Article",
      },
      body: {
        ar: `${a.excerpt?.ar || ""} ${a.summary?.ar || ""} ${articleBodyText(a, "ar")}`,
        en: `${a.excerpt?.en || ""} ${a.summary?.en || ""} ${articleBodyText(a, "en")}`,
      },
      keywords: {
        ar: `مقالة ${cat ? cat.label.ar : ""}`,
        en: `article ${cat ? cat.label.en : ""}`,
      },
    };
  });
}

const ARTICLE_ENTRIES = buildArticleEntries(_SEED_ARTICLES, _SEED_ARTICLE_CATEGORIES);

/* -----------------------------------------------------------------------
 * Build per-language flat indexes with pre-normalized haystacks.
 * -------------------------------------------------------------------- */
const ALL_ENTRIES = [...PAGE_ENTRIES, ...TOOL_ENTRIES, ...ARTICLE_ENTRIES];

function buildLangIndexFromEntries(entries, lang) {
  return (entries || []).map((e) => {
    const title = e.title?.[lang] || "";
    const subtitle = e.subtitle?.[lang] || "";
    const body = e.body?.[lang] || "";
    const keywords = e.keywords?.[lang] || "";
    return {
      id: e.id,
      type: e.type,
      path: e.path,
      title,
      subtitle,
      _nTitle: normalize(title),
      _nSubtitle: normalize(subtitle),
      _nBody: normalize(body),
      _nKeywords: normalize(keywords),
    };
  });
}

/**
 * Build a per-language search index from live CMS data.
 *
 * Accepts `{ tools, toolCategories, articles, articleCategories }`; any of
 * these may be omitted, in which case the static seed data shipped with the
 * frontend bundle is used as a fallback (so initial render never blocks).
 */
export function buildSearchIndex({
  tools,
  toolCategories,
  articles,
  articleCategories,
} = {}) {
  const toolEntries =
    tools !== undefined || toolCategories !== undefined
      ? buildToolEntries(tools || _SEED_TOOLS, toolCategories || _SEED_TOOL_CATEGORIES)
      : TOOL_ENTRIES;
  const articleEntries =
    articles !== undefined || articleCategories !== undefined
      ? buildArticleEntries(
          articles || _SEED_ARTICLES,
          articleCategories || _SEED_ARTICLE_CATEGORIES
        )
      : ARTICLE_ENTRIES;
  const entries = [...PAGE_ENTRIES, ...toolEntries, ...articleEntries];
  return {
    ar: buildLangIndexFromEntries(entries, "ar"),
    en: buildLangIndexFromEntries(entries, "en"),
  };
}

export const INDEX = {
  ar: buildLangIndexFromEntries(ALL_ENTRIES, "ar"),
  en: buildLangIndexFromEntries(ALL_ENTRIES, "en"),
};

/* -----------------------------------------------------------------------
 * Scoring & search
 * -------------------------------------------------------------------- */

const TITLE_WEIGHT = 10;
const SUBTITLE_WEIGHT = 4;
const KEYWORDS_WEIGHT = 3;
const BODY_WEIGHT = 1;

// A little boost if any haystack starts with the query (exact prefix match)
const PREFIX_BOOST = 6;
// Boost if query appears as a word boundary
const WORD_BOUNDARY_BOOST_FACTOR = 1.6;

function countOccurrences(haystack, needle) {
  if (!haystack || !needle) return 0;
  let count = 0;
  let idx = 0;
  while ((idx = haystack.indexOf(needle, idx)) !== -1) {
    count += 1;
    idx += needle.length;
  }
  return count;
}

function hasWordBoundaryMatch(haystack, needle) {
  if (!haystack || !needle) return false;
  // word boundary: start of string, whitespace, or common punctuation before
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[\\s\\u0600-\\u061F\\u060C\\u061B\\u061F·,.;:!?\\-—–/])${escaped}`);
  return re.test(haystack);
}

function scoreEntry(entry, q) {
  let score = 0;

  const titleHits = countOccurrences(entry._nTitle, q);
  const subHits = countOccurrences(entry._nSubtitle, q);
  const kwHits = countOccurrences(entry._nKeywords, q);
  const bodyHits = countOccurrences(entry._nBody, q);

  if (titleHits) {
    score += titleHits * TITLE_WEIGHT;
    if (hasWordBoundaryMatch(entry._nTitle, q)) score *= WORD_BOUNDARY_BOOST_FACTOR;
    if (entry._nTitle.startsWith(q)) score += PREFIX_BOOST;
  }
  if (subHits) score += subHits * SUBTITLE_WEIGHT;
  if (kwHits) score += kwHits * KEYWORDS_WEIGHT;
  if (bodyHits) score += bodyHits * BODY_WEIGHT;

  // Mild bias so short, high-signal results (pages) tend to surface first
  // when scores are roughly tied.
  if (score > 0 && entry.type === "page") score += 0.5;
  return score;
}

/**
 * Run a language-aware search.
 *
 * @param {string} query Raw user query.
 * @param {"ar"|"en"} lang Active site language.
 * @param {{ minChars?: number, limit?: number, index?: {ar:any[],en:any[]} }} [opts]
 *   Pass `index` to search a dynamically-built CMS-backed index (preferred
 *   once live data is available). When omitted we fall back to the static
 *   `INDEX` built from the bundled seed data.
 * @returns {Array<{id,type,path,title,subtitle,score}>}
 *   Empty when the query is shorter than `minChars` (default 3).
 */
export function searchEntries(query, lang, opts = {}) {
  const { minChars = 3, limit = 8, index } = opts;
  if (!query) return [];
  const q = normalize(query);
  if (q.length < minChars) return [];

  const indexPack = index || INDEX;
  const pool = indexPack[lang] || indexPack.ar;
  const scored = [];
  for (const e of pool) {
    const s = scoreEntry(e, q);
    if (s > 0) {
      scored.push({
        id: e.id,
        type: e.type,
        path: e.path,
        title: e.title,
        subtitle: e.subtitle,
        score: s,
      });
    }
  }
  scored.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));
  return scored.slice(0, limit);
}

/** Group ordered results by type for nicer rendering. Stable ordering. */
export function groupResults(results) {
  const groups = { page: [], tool: [], article: [] };
  for (const r of results) {
    if (groups[r.type]) groups[r.type].push(r);
  }
  return groups;
}

export const GROUP_LABELS = {
  ar: { page: "الصفحات", tool: "الأدوات", article: "المقالات" },
  en: { page: "Pages", tool: "Tools", article: "Articles" },
};
